import React from 'react';

import { NavItem } from './environments';

/**
 * The child space's icons and rail, from the redesign prototype.
 *
 * The prototype paints these at runtime: `<i data-ic="home">` placeholders are
 * filled by a script from an ICONS map. Rendering the placeholders statically
 * would give five empty squares, so the map is ported and the svg is real —
 * the same paths, the same stroke, wrapped the way `ico()` wraps them.
 *
 * Each item carries its own colour through `--c`, which is how the child rail
 * is drawn: one hue per destination rather than one accent for the whole app.
 *
 * Source: docs/redesign/IQMaxx/2_Student_Nino.html
 */

const KidIcon: React.FC<{ paths: string; size?: number }> = ({ paths, size = 21 }) => (
  <svg
    className="ic"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    width={size}
    height={size}
    dangerouslySetInnerHTML={{ __html: paths }}
  />
);

/** The prototype's ICONS map, verbatim. */
export const KID_ICON_PATHS: Record<string, string> = {
  home: '<path d="M3 10.5 12 3l9 7.5"/><path d="M5.5 9.5V20a1 1 0 0 0 1 1H10v-5.5h4V21h3.5a1 1 0 0 0 1-1V9.5"/>',
  books:
    '<path d="M12 6.6C10.3 5.2 8 4.6 4.4 4.6a1 1 0 0 0-1 1v11.6a1 1 0 0 0 1 1c3.6 0 5.9.6 7.6 2 1.7-1.4 4-2 7.6-2a1 1 0 0 0 1-1V5.6a1 1 0 0 0-1-1c-3.6 0-5.9.6-7.6 2Z"/><path d="M12 6.6v13"/>',
  search: '<circle cx="11" cy="11" r="6.5"/><path d="m20 20-3.6-3.6"/>',
  star: '<path d="m12 3.6 2.6 5.5 6 .8-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6L3.4 9.9l6-.8z"/>',
  medal:
    '<circle cx="12" cy="15" r="5.5"/><path d="M12 12.6v4.8M9.8 15h4.4" stroke-width="1.4"/><path d="M8.5 9.6 6 3h4l2 4M15.5 9.6 18 3h-4l-2 4"/>',
  palette:
    '<path d="M12 21a9 9 0 1 1 9-9c0 2-1.6 2.6-3 2.6h-1.4a2 2 0 0 0-1.3 3.5c.4.4.4 1.2-.1 1.6A4 4 0 0 1 12 21Z"/><circle cx="8" cy="10.5" r="1.1" fill="currentColor" stroke="none"/><circle cx="12" cy="7.8" r="1.1" fill="currentColor" stroke="none"/><circle cx="16" cy="10.5" r="1.1" fill="currentColor" stroke="none"/>',
  bell: '<path d="M18 15.5V11a6 6 0 1 0-12 0v4.5L4.6 18h14.8z"/><path d="M9.8 20.6a2.4 2.4 0 0 0 4.4 0"/>',
  help: '<circle cx="12" cy="12" r="9"/><path d="M9.4 9.4a2.7 2.7 0 1 1 3.4 3.3c-.6.2-.8.7-.8 1.3v.4"/><circle cx="12" cy="17.2" r="1" fill="currentColor" stroke="none"/>',
  wave: '<path d="M9 11V5.4a1.4 1.4 0 1 1 2.8 0V10M11.8 10V4.6a1.4 1.4 0 1 1 2.8 0V10M14.6 10.4V6.6a1.4 1.4 0 1 1 2.8 0V14a7 7 0 0 1-7 7 7 7 0 0 1-7-7v-1.6a1.4 1.4 0 1 1 2.8 0V14"/>',
  menu: '<path d="M4 7h16M4 12h16M4 17h16"/>',
  calendar:
    '<rect x="3.2" y="5" width="17.6" height="16" rx="3"/><path d="M3.2 10h17.6M8 3v4M16 3v4"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5.3l3.4 2"/>',
  check: '<path d="m5 12.6 4.6 4.4L19 7.4"/>',
  lock: '<rect x="5" y="10.5" width="14" height="10.5" rx="3"/><path d="M8.4 10.5V7.8a3.6 3.6 0 0 1 7.2 0v2.7"/>',
  note: '<path d="M5 4.6h9.6L19 9v10.4a1.6 1.6 0 0 1-1.6 1.6H5a1.6 1.6 0 0 1-1.6-1.6V6.2A1.6 1.6 0 0 1 5 4.6Z"/><path d="M14 4.6V9h4.6M7.4 13h8M7.4 16.6h5.4"/>',
  map: '<path d="M9 4.4 3.4 6.8v13L9 17.4l6 2.4 5.6-2.4v-13L15 6.8z"/><path d="M9 4.4v13M15 6.8v13"/>',
  flame:
    '<path d="M12 3.2s5.4 4 5.4 9a5.4 5.4 0 0 1-10.8 0c0-1.6.7-2.9 1.5-3.9.3 1.2 1 2 1.9 2 0-3 2-7.1 2-7.1Z"/>',
  bulb: '<path d="M9.4 17.6h5.2M10.2 20.4h3.6"/><path d="M12 3.4a6 6 0 0 0-3.4 11c.5.4.8 1 .8 1.6h5.2c0-.6.3-1.2.8-1.6A6 6 0 0 0 12 3.4Z"/>',
  pen: '<path d="M4 20.2 4.9 16 16.3 4.6a2.1 2.1 0 0 1 3 3L7.9 19z"/><path d="m14.4 6.5 3.1 3.1"/>',
  laptop: '<rect x="3.4" y="5" width="17.2" height="11" rx="2"/><path d="M2 19.6h20"/>',
  numbers:
    '<rect x="3.4" y="3.4" width="17.2" height="17.2" rx="4"/><path d="M6.8 8.6h3.4M8.5 6.9v3.4M13.8 8.6h3.4M6.8 15.4h3.4M14 13.7l3 3M17 13.7l-3 3"/>',
  target:
    '<circle cx="12" cy="12" r="8.6"/><circle cx="12" cy="12" r="4.8"/><circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none"/>',
  sunrise:
    '<path d="M12 3v3.4M5.6 6.4 7.7 8.5M2.6 14h2.8M18.6 14h2.8M16.3 8.5l2.1-2.1"/><path d="M8 14a4 4 0 0 1 8 0"/><path d="M3.4 18.4h17.2"/>',
  hands:
    '<path d="M8.6 12.6 6.4 10.4a1.8 1.8 0 0 0-2.6 2.5l4.3 4.4a5.6 5.6 0 0 0 4 1.7h3.5"/><path d="M15.4 12.6l2.2-2.2a1.8 1.8 0 0 1 2.6 2.5"/><path d="M8.6 12.6V6.4a1.7 1.7 0 1 1 3.4 0v4.4M12 10.8V5.6a1.7 1.7 0 1 1 3.4 0v6.4"/>',
  puzzle:
    '<path d="M10 4.4h4v2a1.8 1.8 0 1 0 3.6 0v-2h2v4h-2a1.8 1.8 0 1 0 0 3.6h2v4.4h-4.4v-2a1.8 1.8 0 1 0-3.6 0v2H7.2v-4.4h-2a1.8 1.8 0 1 1 0-3.6h2V4.4z"/>',
  rocket:
    '<path d="M12 3c3.4 2.4 5.2 6 5.2 9.6L14.6 16H9.4l-2.6-3.4C6.8 9 8.6 5.4 12 3Z"/><circle cx="12" cy="10" r="1.9"/><path d="M9.4 16 7 20l3-1 2 2 2-2 3 1-2.4-4"/>',
  crown:
    '<path d="M4 8.6 7.4 13l4.6-6.6L16.6 13 20 8.6v9a1.6 1.6 0 0 1-1.6 1.6H5.6A1.6 1.6 0 0 1 4 17.6z"/>',
  sparkle:
    '<path d="M12 3.6 13.5 9l5.4 1.5-5.4 1.5L12 17.4l-1.5-5.4L5.1 10.5 10.5 9z"/><path d="M18.6 16.2l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z"/>',
  frog: '<path d="M5.2 12.6a6.8 6.8 0 0 1 13.6 0v2.2a4.4 4.4 0 0 1-4.4 4.4H9.6a4.4 4.4 0 0 1-4.4-4.4z"/><circle cx="8.6" cy="7.4" r="2.8"/><circle cx="15.4" cy="7.4" r="2.8"/><circle cx="8.6" cy="7.6" r="1" fill="currentColor" stroke="none"/><circle cx="15.4" cy="7.6" r="1" fill="currentColor" stroke="none"/><path d="M9.6 15.2a3.4 3.4 0 0 0 4.8 0"/>',
  person: '<circle cx="12" cy="8.4" r="4"/><path d="M4.6 20.4a7.4 7.4 0 0 1 14.8 0"/>',
};

/**
 * The learner's rail — the Sprint 3 Student prototype's own destinations
 * (09_PROTOTIPOS_v2-1, "Vista alumno"): my classes, my calendar, my progress,
 * past classes, plus UC-S02..S06: explore, requests, profile. Saved and awards
 * have nothing behind them (DEC-S2-S03-08, S02-06) and stay off it.
 */
const kidItem = (
  key: string,
  label: string,
  to: string,
  icon: string,
  colour: string,
): NavItem => ({
  key,
  label,
  to,
  icon: (
    <span style={{ ['--c' as string]: colour } as React.CSSProperties}>
      <KidIcon paths={KID_ICON_PATHS[icon]} />
    </span>
  ),
});

export const KID_NAV: NavItem[] = [
  kidItem('books', 'Mis clases', '/student', 'books', '#F97316'),
  kidItem('explore', 'Explorar', '/student/explore', 'search', '#0EA5E9'),
  kidItem('requests', 'Mis solicitudes', '/student/requests', 'note', '#D97706'),
  kidItem('calendar', 'Mi calendario', '/student/calendar', 'calendar', '#3D7BFF'),
  kidItem('progress', 'Mi progreso', '/student/progress', 'sunrise', '#22A97F'),
  kidItem('past', 'Clases pasadas', '/student/past', 'clock', '#7C5CF0'),
  // MC-05 — notices reach a learner here, inside IQMaxx, and nowhere else.
  kidItem('notices', 'Mis avisos', '/student/notices', 'bell', '#E5484D'),
  kidItem('profile', 'Mi perfil', '/student/profile', 'person', '#14B8A6'),
];
